import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";

export interface BnColumn {
  /** Key into each row object. */
  key: string;
  label: string;
  align?: "left" | "right" | "center";
  /** CSS width, e.g. "1px" (shrink) or "40%". */
  width?: string;
  /** Render this column's cells in the monospace font. */
  mono?: boolean;
}

/** A row is a plain object; a cell value may be a string, number, or a lit template. */
export type BnRow = Record<string, unknown>;

/**
 * bn-table is a declarative data table styled in the DS. Set `.columns` and
 * `.rows` as properties (rows may carry lit TemplateResults as cell values, e.g.
 * a bn-badge or an action button). It renders a mono uppercase header and
 * hairline row separators. When there are no rows it shows `empty`.
 *
 *   <bn-table .columns=${cols} .rows=${rows} empty="No keys yet"></bn-table>
 */
@customElement("bn-table")
export class BnTable extends LitElement {
  @property({ attribute: false }) columns: BnColumn[] = [];
  @property({ attribute: false }) rows: BnRow[] = [];
  @property() empty = "Nothing here yet.";

  static override styles = css`
    :host {
      display: block;
      overflow-x: auto;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
    }
    thead th {
      background: var(--bino-surface-subtle);
      color: var(--bino-text-secondary);
      font-family: var(--bino-font-mono);
      font-weight: 600;
      font-size: var(--bino-font-size-xs);
      letter-spacing: 0.08em;
      text-transform: uppercase;
      text-align: left;
      padding: 0.5rem 0.75rem;
      border-bottom: 1px solid var(--bino-border);
      white-space: nowrap;
    }
    tbody td {
      padding: 0.625rem 0.75rem;
      border-bottom: 1px solid var(--bino-border);
      vertical-align: middle;
    }
    tbody tr:last-child td {
      border-bottom: none;
    }
    tbody tr:hover td {
      background: var(--bino-surface-hover);
    }
    td.mono {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .empty {
      padding: var(--bino-space-xl);
      text-align: center;
      color: var(--bino-text-secondary);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
    }
  `;

  override render() {
    if (this.rows.length === 0) {
      return html`<div class="empty">${this.empty}</div>`;
    }
    return html`
      <table>
        <thead>
          <tr>
            ${this.columns.map(
              (c) =>
                html`<th
                  style=${[c.width ? `width:${c.width}` : "", c.align ? `text-align:${c.align}` : ""]
                    .filter(Boolean)
                    .join(";")}
                >
                  ${c.label}
                </th>`,
            )}
          </tr>
        </thead>
        <tbody>
          ${this.rows.map(
            (row) => html`<tr>
              ${this.columns.map(
                (c) =>
                  html`<td
                    class=${c.mono ? "mono" : ""}
                    style=${c.align ? `text-align:${c.align}` : ""}
                  >
                    ${row[c.key] as unknown}
                  </td>`,
              )}
            </tr>`,
          )}
        </tbody>
      </table>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-table": BnTable;
  }
}
